/**
 * ChoreCard — én oppgave på tavla, som en TaskRow (#154, steg 4b).
 *
 * Tidligere en loddrett flis (Plan 02-09, UI-SPEC §2). Nå en rad som i
 * design/screens/Stell_Oppgaver.dc.html: ikonflis 44 px, tittel, metalinje og
 * handlingen i en spalte med fast bredde 96 px.
 *
 * Variantene kommer fra instance.status × viewer, som før:
 *
 *   available           → «Ta» (solid). Aldersgrense (CHORE-05) eller en
 *                          oppgave som er låst til andre gjør knappen inaktiv,
 *                          og metalinja sier hvorfor («For 12+», «Kun for …»).
 *   claimed-by-me       → «Fullfør» (primary), og metalinja «Din nå · holdes i
 *                          N t» fra `claim_expires_at`. Lilla kant.
 *   claimed-by-other    → ingen handling; metalinja «{nick} tok den».
 *   awaiting-approval   → «Venter» (StatusPill) der knappen sto.
 *   expired             → stiplet kant, «Utløpt — tilbake i bunken».
 *
 * Teksten bærer alltid betydningen; kant- og tekstfarge er aldri eneste
 * kjennetegn. All tekst går gjennom `t('chores.*')` (MED-7).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ChoreIcon, type TintSlot } from '../../components/primitives/ChoreIcon';
import { StatusPill } from '../../components/primitives/StatusPill';
import { TaskRow } from '../../components/primitives/TaskRow';
import { describeDue, isUrgentDue } from './homeworkDue';
import { describeRecurrence, formatRecurrence } from './recurrenceHint';
import {
  AWAITING_LINE,
  CompleteButton,
  HeldByMeMeta,
  MINE_LINE,
  TakeButton,
  joinMeta,
} from './taskActions';
import type { PoolChoreInstance } from './useChoresQuery';

// Re-export for tests so the type can be imported as ChoreInstance from this module.
// `due_at` and `recurrence` are optional so the pool/my feeds (which don't join
// the template's recurrence) still satisfy the prop type; each meta part simply
// renders nothing when its field is absent.
export type ChoreInstance = PoolChoreInstance & {
  due_at?: string | null;
  recurrence?: string | null;
};

export interface Viewer {
  id: string;
  shortName?: string | null;
  ageYears: number;
}

type Variant =
  | 'available'
  | 'claimed-by-me'
  | 'claimed-by-other'
  | 'awaiting-approval-by-me'
  | 'expired';

interface ChoreCardProps {
  instance: ChoreInstance;
  viewer: Viewer;
  /** Triggered when the kid presses "Ta" (available variant). */
  onClaim?: () => void;
  /** Triggered when the kid presses "Fullfør" (claimed-by-me variant). */
  onComplete?: () => void;
  /** Bound to the claim mutation's isPending — the spinner waits for the server (D-12). */
  spinner?: boolean;
}

function resolveVariant(instance: ChoreInstance, viewer: Viewer): Variant {
  if (instance.status === 'expired') return 'expired';
  if (instance.status === 'available') return 'available';
  if (instance.status === 'awaiting_approval') {
    return instance.claimed_by === viewer.id ? 'awaiting-approval-by-me' : 'claimed-by-other';
  }
  if (instance.status === 'claimed') {
    return instance.claimed_by === viewer.id ? 'claimed-by-me' : 'claimed-by-other';
  }
  // completed / rejected fall back to expired-style read-only card.
  return 'expired';
}

/**
 * Kantfargen (TaskRow `line`). Lilla = din, som skissens «Mine nå»-kort;
 * amber = venter på en voksen. Utløpt får i tillegg stiplet kant.
 */
const LINE: Record<Variant, string | undefined> = {
  available: undefined,
  'claimed-by-me': MINE_LINE,
  'claimed-by-other': undefined,
  'awaiting-approval-by-me': AWAITING_LINE,
  expired: undefined,
};

export function ChoreCard({
  instance,
  viewer,
  onClaim,
  onComplete,
  spinner = false,
}: ChoreCardProps): ReactNode {
  const { t } = useTranslation();
  const variant = resolveVariant(instance, viewer);
  const ageGated = variant === 'available' && instance.age_gated;
  // Fase 1: a restricted card the viewer is not assigned to. Either gate
  // disables the claim button; the kid still sees the card (CHORE-05 pattern).
  const assignmentLocked = variant === 'available' && !!instance.assignment_locked;
  const locked = ageGated || assignmentLocked;
  const assigneeNames = (instance.assignee_short_names ?? []).filter(Boolean);
  const tintSlot = (instance.tint_snapshot ?? 'sage') as TintSlot | string;

  // Frist og gjentakelse er råd, ikke handling. Fristen er den som haster, så
  // den står først av de to; en frist i dag, i morgen eller passert er amber
  // (prinsipp 3 i HANDOFF.md), og teksten sier det samme.
  const due = describeDue(instance.due_at);
  const dueText =
    due.kind === 'overdue'
      ? t('chores.due.overdue')
      : due.kind === 'today'
        ? t('chores.due.today')
        : due.kind === 'tomorrow'
          ? t('chores.due.tomorrow')
          : due.kind === 'date'
            ? t('chores.due.date', { date: due.date })
            : null;
  const dueUrgent = isUrgentDue(due);
  const repeatText = formatRecurrence(describeRecurrence(instance.recurrence), t);

  const points = t('chores.points', { points: instance.point_value_snapshot });

  const assigneeText =
    variant === 'available' && assigneeNames.length > 0 ? (
      <span key="assignees" data-testid="chore-card-assignees">
        {assignmentLocked
          ? t('chores.assignmentLocked', { names: assigneeNames.join(', ') })
          : t('chores.assignedTo', { names: assigneeNames.join(', ') })}
      </span>
    ) : null;

  const dueRepeat =
    dueText || repeatText ? (
      <span key="due" data-testid="chore-card-meta">
        {joinMeta([
          dueText ? (
            <span key="due" className={dueUrgent ? 'font-bold text-warn' : undefined}>
              {dueText}
            </span>
          ) : null,
          repeatText,
        ])}
      </span>
    ) : null;

  let meta: ReactNode;
  let action: ReactNode = null;
  switch (variant) {
    case 'available':
      // Metalinja kuttes fra høyre, så det viktigste står først: poeng, så
      // hvorfor knappen er inaktiv, så fristen, og «Tildelt …» sist.
      meta = joinMeta([
        points,
        ageGated ? t('chores.ageGatedFor', { minAge: instance.min_age_snapshot }) : null,
        assignmentLocked ? assigneeText : null,
        dueRepeat,
        assignmentLocked ? null : assigneeText,
      ]);
      action = (
        <TakeButton testIdPrefix="chore-card" locked={locked} pending={spinner} onClaim={onClaim} />
      );
      break;
    case 'claimed-by-me':
      meta = joinMeta([
        <HeldByMeMeta key="held" claimExpiresAt={instance.claim_expires_at} />,
        dueRepeat,
      ]);
      action = <CompleteButton testIdPrefix="chore-card" pending={spinner} onComplete={onComplete} />;
      break;
    case 'claimed-by-other':
      meta = joinMeta([
        points,
        <span key="taken" className="font-bold text-good">
          {t('chores.takenBy', { nick: instance.claimed_by_short_name ?? '' })}
        </span>,
      ]);
      break;
    case 'awaiting-approval-by-me':
      meta = joinMeta([points, dueRepeat]);
      action = <StatusPill kind="awaiting" className="justify-center" />;
      break;
    case 'expired':
      meta = t('chores.expired');
      break;
  }

  return (
    <TaskRow
      data-testid="chore-card"
      data-variant={variant}
      aria-busy={spinner || undefined}
      line={LINE[variant]}
      className={variant === 'expired' ? 'border-dashed' : undefined}
      icon={<ChoreIcon code={instance.icon_key ?? 'sparkle'} tintSlot={tintSlot} size={44} />}
      title={instance.title_snapshot}
      meta={meta}
      action={action}
    />
  );
}
